import {
  EnvironmentProviders,
  importProvidersFrom,
  inject,
  provideEnvironmentInitializer,
  Provider,
} from '@angular/core';
import {
  ApplicationStore,
  SOFTLINE_CONFIG_OFFLINE_FEATURE,
  SOFTLINE_FEATURE_APPLICATIONS,
} from '@softline/application';
import {
  provideSoftAppsModule,
  withLazySoftAppsModuleTranslation,
  withSoftAppsModuleRoutes,
} from '@softapps/core';
import {
  ConnectionHttpService,
  SOFTLINE_SERVICE_HTTP,
  SOFTLINE_STORE_ENTITY_SERVICE,
  SOFTLINE_STORE_REMOTE_OBJECT_SERVICE,
  SoftlineEntityService,
  SoftlineObjectService,
  Store,
  StoreModule,
} from '@softline/core';
import { SOFTLINE_FEATURE_TRANSLATION, TranslationStore } from '@softline/ui-core';
import { default as de } from '../i18n/de.json';
import { default as deTitles } from '../i18n/de.titles.json';
import { energietransportRoutes } from './energietransport.routes';
import { provideEnergietransportGlobals } from './energietransport.globals';
import { AuftragResourceService } from './services/auftrag-resource.service';
import { AuftragStore, StationStore, ZusatzartikelStore } from './store';
import {
  SOFTLINE_API_TRANSPORTAUFTRAG,
  SOFTLINE_API_TRANSPORTAUFTRAG_ZUSATZARTIKEL,
} from './transport.api';
import { SOFTLINE_PERMISSION_TRANSPORTAUFTRAG } from './transport.permissions';
import {
  SOFTLINE_FEATURE_TRANSPORTAUFTRAG,
  SOFTLINE_FEATURE_TRANSPORTAUFTRAG_STATIONEN,
  SOFTLINE_FEATURE_TRANSPORTAUFTRAG_ZUSATZARTIKEL,
} from './transport.shared';

export const energietransportProviders: (Provider | EnvironmentProviders)[] = [
  provideEnvironmentInitializer(() => {
    const store = inject(Store);
    store.commit(SOFTLINE_FEATURE_TRANSLATION, TranslationStore.mutations.add, {
      module: 'energietransport',
      language: 'de',
      translations: de,
    });
    store.commit(SOFTLINE_FEATURE_APPLICATIONS, ApplicationStore.mutations.add, {
      name: '#TRANSPORT.APP',
      icon: 'fa-regular fa-truck-bolt',
      route: '/energietransport',
      permission: SOFTLINE_PERMISSION_TRANSPORTAUFTRAG,
    });
  }),
  {
    provide: SOFTLINE_CONFIG_OFFLINE_FEATURE,
    useValue: {
      featureName: SOFTLINE_FEATURE_TRANSPORTAUFTRAG,
      fields: ['ids', 'entities', 'preparedChanges'],
    },
    multi: true,
  },
  {
    provide: SOFTLINE_CONFIG_OFFLINE_FEATURE,
    useValue: {
      featureName: SOFTLINE_FEATURE_TRANSPORTAUFTRAG_STATIONEN,
      fields: ['ids', 'entities', 'preparedChanges'],
    },
    multi: true,
  },
  {
    provide: SOFTLINE_CONFIG_OFFLINE_FEATURE,
    useValue: {
      featureName: SOFTLINE_FEATURE_TRANSPORTAUFTRAG_ZUSATZARTIKEL,
      fields: ['data'],
    },
    multi: true,
  },
  AuftragResourceService,
  importProvidersFrom(
    StoreModule.forFeature({
      name: SOFTLINE_FEATURE_TRANSPORTAUFTRAG,
      feature: AuftragStore.feature,
      providers: [
        {
          provide: SOFTLINE_STORE_ENTITY_SERVICE,
          useFactory: (service: ConnectionHttpService) =>
            new SoftlineEntityService(service, SOFTLINE_API_TRANSPORTAUFTRAG),
          deps: [SOFTLINE_SERVICE_HTTP],
        },
      ],
    }),
    StoreModule.forFeature({
      name: SOFTLINE_FEATURE_TRANSPORTAUFTRAG_STATIONEN,
      feature: StationStore.feature,
    }),
    StoreModule.forFeature({
      name: SOFTLINE_FEATURE_TRANSPORTAUFTRAG_ZUSATZARTIKEL,
      feature: ZusatzartikelStore.feature,
      providers: [
        {
          provide: SOFTLINE_STORE_REMOTE_OBJECT_SERVICE,
          useFactory: (service: ConnectionHttpService) =>
            new SoftlineObjectService(
              service,
              SOFTLINE_API_TRANSPORTAUFTRAG_ZUSATZARTIKEL
            ),
          deps: [SOFTLINE_SERVICE_HTTP],
        },
      ],
    })
  ),
];

export function provideEnergietransportModule() {
  return [
    provideSoftAppsModule(
      {
        name: 'wws/energietransport',
        title: '#TRANSPORT.APP',
        icon: 'fa-regular fa-truck-bolt',
        route: '/energietransport',
        permission: SOFTLINE_PERMISSION_TRANSPORTAUFTRAG,
      },
      withSoftAppsModuleRoutes({ app: energietransportRoutes }),
      withLazySoftAppsModuleTranslation({
        eager: { de: deTitles },
        lazy: { de: () => import('../i18n/de.json') },
      })
    ),
    ...provideEnergietransportGlobals(),
  ];
}
